import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit';
import { ClientAuthState } from '../objects/clientAuthState';
import { ClientAuthSettingsResponse, ClientMfaAuthItem, DisconnectUserRequest } from '../objects/clientAuthModels';
import {
    EMPTY_CLIENT_AUTH_SETTINGS_LIST_CRITERIA,
    EMPTY_CLIENT_AUTH_USERS_LIST_CONFIGURATION,
    EMPTY_CLIENT_AUTH_USERS_LIST_CRITERIA,
} from '../constant/clientAuthConstant';
import { ClientAuthSettingsListCriteria, ClientAuthUsersListCriteria } from '../objects/clientAuthList';
import { Preferences } from '../../profile/objects/preferences';
import { setClientAuthPreference } from '../utils/setClientAuthPreference';

const initialState: ClientAuthState = {
    settings: null,
    users: [],
    settings_loading: false,
    users_loading: false,
    users_refresh: false,
    users_list_criteria: EMPTY_CLIENT_AUTH_USERS_LIST_CRITERIA,
    settings_list_criteria: EMPTY_CLIENT_AUTH_SETTINGS_LIST_CRITERIA,
    users_list_configuration: EMPTY_CLIENT_AUTH_USERS_LIST_CONFIGURATION,
    users_selected: [],
};

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const updateClientAuthSettingsReducer = (
    state: ClientAuthState,
    action: PayloadAction<ClientAuthSettingsResponse | null>,
): ClientAuthState => ({
    ...state,
    settings: action.payload,
});

const updateClientAuthUsersReducer = (
    state: ClientAuthState,
    action: PayloadAction<ClientMfaAuthItem[]>,
): ClientAuthState => ({
    ...state,
    users: action.payload,
    users_selected: [],
});

const setClientAuthUsersSelectedReducer = (
    state: ClientAuthState,
    action: PayloadAction<DisconnectUserRequest[]>,
): ClientAuthState => ({
    ...state,
    users_selected: action.payload,
});

const toggleClientAuthUserSelectedReducer = (
    state: ClientAuthState,
    action: PayloadAction<DisconnectUserRequest>,
): ClientAuthState => {
    const selected = [...state.users_selected];
    const index = selected.findIndex(
        (s) => s.user_id === action.payload.user_id && s.authorized_environment === action.payload.authorized_environment,
    );
    if (index === -1) {
        selected.push(action.payload);
    } else {
        selected.splice(index, 1);
    }
    return { ...state, users_selected: selected };
};

const updateClientAuthSettingsLoadingReducer = (
    state: ClientAuthState,
    action: PayloadAction<boolean>,
): ClientAuthState => ({
    ...state,
    settings_loading: action.payload,
});

const updateClientAuthUsersLoadingReducer = (
    state: ClientAuthState,
    action: PayloadAction<boolean>,
): ClientAuthState => ({
    ...state,
    users_loading: action.payload,
});

const toggleClientAuthUsersRefreshReducer = (state: ClientAuthState): ClientAuthState => ({
    ...state,
    users_refresh: !state.users_refresh,
});

const updateClientAuthUsersListCriteriaReducer = (
    state: ClientAuthState,
    action: PayloadAction<ClientAuthUsersListCriteria>,
): ClientAuthState => ({
    ...state,
    users_list_criteria: action.payload,
});

const updateClientAuthSettingsListCriteriaReducer = (
    state: ClientAuthState,
    action: PayloadAction<ClientAuthSettingsListCriteria>,
): ClientAuthState => ({
    ...state,
    settings_list_criteria: action.payload,
});

const setClientAuthUsersColumnsReducer = (
    state: ClientAuthState,
    action: PayloadAction<ClientAuthState['users_list_configuration']['columns']>,
): ClientAuthState => ({
    ...state,
    users_list_configuration: { ...state.users_list_configuration, columns: action.payload },
});

const toggleClientAuthUsersColumnReducer = (
    state: ClientAuthState,
    action: PayloadAction<number>,
): ClientAuthState => {
    const columns = [...state.users_list_configuration.columns];
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return {
        ...state,
        users_list_configuration: { ...state.users_list_configuration, columns },
    };
};

const addClientAuthUsersListConfigurationPreferenceReducer = (
    state: ClientAuthState,
    action: PayloadAction<Preferences[]>,
): ClientAuthState => ({
    ...state,
    users_list_configuration: {
        ...state.users_list_configuration,
        preferences: [...state.users_list_configuration.preferences, ...action.payload],
    },
});

const setClientAuthUsersListConfigurationPreferenceReducer = (
    state: ClientAuthState,
    action: PayloadAction<Preferences[]>,
): ClientAuthState => ({
    ...state,
    users_list_configuration: {
        ...state.users_list_configuration,
        preferences: action.payload,
    },
});

const setPreferenceReducer = (state: ClientAuthState, action: PayloadAction<Preferences[]>): ClientAuthState => {
    const pref = setClientAuthPreference(action.payload, state.users_list_configuration.columns);
    return {
        ...state,
        users_list_configuration: {
            ...state.users_list_configuration,
            preferences: pref.preferences,
            columns: pref.columns,
        },
        users_list_criteria: {
            ...state.users_list_criteria,
            order_by: pref.order_by,
            order: pref.order,
        },
    };
};

export const clientAuth = createSlice({
    name: 'client_auth',
    initialState,
    reducers: {
        updateClientAuthSettings: updateClientAuthSettingsReducer,
        updateClientAuthUsers: updateClientAuthUsersReducer,
        updateClientAuthSettingsLoading: updateClientAuthSettingsLoadingReducer,
        updateClientAuthUsersLoading: updateClientAuthUsersLoadingReducer,
        toggleClientAuthUsersRefresh: toggleClientAuthUsersRefreshReducer,
        updateClientAuthUsersListCriteria: updateClientAuthUsersListCriteriaReducer,
        updateClientAuthSettingsListCriteria: updateClientAuthSettingsListCriteriaReducer,
        setClientAuthUsersColumns: setClientAuthUsersColumnsReducer,
        toggleClientAuthUsersColumn: toggleClientAuthUsersColumnReducer,
        addClientAuthUsersListConfigurationPreference: addClientAuthUsersListConfigurationPreferenceReducer,
        setClientAuthUsersListConfigurationPreference: setClientAuthUsersListConfigurationPreferenceReducer,
        setClientAuthUsersSelected: setClientAuthUsersSelectedReducer,
        toggleClientAuthUserSelected: toggleClientAuthUserSelectedReducer,
    },
    extraReducers: (builder) => {
        builder.addCase(setPreferences, setPreferenceReducer);
    },
});

export const {
    updateClientAuthSettings,
    updateClientAuthUsers,
    updateClientAuthSettingsLoading,
    updateClientAuthUsersLoading,
    toggleClientAuthUsersRefresh,
    updateClientAuthUsersListCriteria,
    updateClientAuthSettingsListCriteria,
    setClientAuthUsersColumns,
    toggleClientAuthUsersColumn,
    addClientAuthUsersListConfigurationPreference,
    setClientAuthUsersListConfigurationPreference,
    setClientAuthUsersSelected,
    toggleClientAuthUserSelected,
} = clientAuth.actions;
